import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  getSocietyAdminData,
  getAdminSocietyDetail,
  getFlatMembers,
  flatMemberRemovalCheck,
  removeFlatMember,
  adminAddFlatMember,
  getBlocks,
  societyAdminExportMembers,
  updateFlat,
  AdminSocietyDetail,
  AdminSocietyFlat,
  FlatMemberInfo,
  FlatMemberRole,
  RemovalCheckData,
  Block,
} from '../services/api';
import {
  HiSearch,
  HiUserGroup,
  HiRefresh,
  HiExclamation,
  HiTrash,
  HiChevronDown,
  HiChevronUp,
  HiUsers,
  HiX,
  HiPlus,
  HiDownload,
} from '../components/Icons';
import { downloadOnboardExcel, onboardExportFileName } from '../utils/onboardExcel';
import { FLAT_TYPE_MAX_LENGTH } from '../utils/flatTypeNormalize';
import './SocietyAdminFlats.css';

type RoleCategory = 'owner' | 'co_owner' | 'tenant' | 'family';

function normalizeRoleCategory(role: string): RoleCategory {
  const r = role.toLowerCase().replace(/[\s-]+/g, '_');
  if (r === 'co_owner') return 'co_owner';
  if (r === 'owner') return 'owner';
  if (r.includes('family')) return 'family';
  return 'tenant';
}

function roleBadgeLabel(cat: RoleCategory): string {
  if (cat === 'owner') return 'Owner';
  if (cat === 'co_owner') return 'Co-owner';
  if (cat === 'family') return 'Family';
  return 'Tenant';
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function formatRupees(amount: number): string {
  return '\u20B9' + amount.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

const SocietyAdminFlats: React.FC = () => {
  const adminData = getSocietyAdminData();
  const societyId = adminData?.societyId ?? 0;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState<AdminSocietyDetail | null>(null);
  const [blocks, setBlocks] = useState<Block[]>([]);

  const [searchQuery, setSearchQuery] = useState('');
  const [blockFilter, setBlockFilter] = useState<string>('all');

  const [expandedFlatIds, setExpandedFlatIds] = useState<Set<number>>(() => new Set());
  const [membersCache, setMembersCache] = useState<Record<number, FlatMemberInfo[]>>({});
  const [membersLoadingIds, setMembersLoadingIds] = useState<Set<number>>(() => new Set());
  const [membersErrorByFlat, setMembersErrorByFlat] = useState<Record<number, string>>({});
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const [flatTypeDrafts, setFlatTypeDrafts] = useState<Record<number, string>>({});
  const [flatTypeSavingIds, setFlatTypeSavingIds] = useState<Set<number>>(() => new Set());
  const [flatTypeErrorById, setFlatTypeErrorById] = useState<Record<number, string>>({});

  const [removalModal, setRemovalModal] = useState<{
    flat: AdminSocietyFlat;
    member: FlatMemberInfo;
    checkData: RemovalCheckData | null;
    checkLoading: boolean;
    checkError: string;
    removing: boolean;
    reason: string;
  } | null>(null);

  const [addModal, setAddModal] = useState<{
    flat: AdminSocietyFlat;
    members: FlatMemberInfo[];
    phone: string;
    name: string;
    roleInFlat: FlatMemberRole;
    linkedToUserId: number | '';
    submitting: boolean;
    error: string;
  } | null>(null);

  const fetchData = useCallback(async () => {
    if (!societyId) {
      setLoading(false);
      setError('No society selected. Please select a society from the menu.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const [societyRes, blocksRes] = await Promise.all([
        getAdminSocietyDetail(societyId),
        getBlocks(societyId),
      ]);
      setData(societyRes.data);
      setBlocks(blocksRes.data);
    } catch (err: any) {
      setError(err.message || 'Failed to load flats data');
    } finally {
      setLoading(false);
    }
  }, [societyId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExportMembers = async () => {
    if (!societyId || !data?.society) return;
    setExportError('');
    setExporting(true);
    try {
      const res = await societyAdminExportMembers(societyId);
      const fileName = onboardExportFileName(data.society.name, societyId);
      downloadOnboardExcel(
        {
          society: res.data.society,
          blocks: res.data.blocks,
          flats: res.data.flats,
        },
        fileName,
      );
    } catch (err: unknown) {
      setExportError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const getFlatTypeDraft = (flat: AdminSocietyFlat): string => {
    if (flatTypeDrafts[flat.id] !== undefined) return flatTypeDrafts[flat.id]!;
    return flat.flat_type ?? '';
  };

  const saveFlatType = async (flat: AdminSocietyFlat) => {
    const draft = getFlatTypeDraft(flat).trim();
    const current = (flat.flat_type ?? '').trim();
    if (draft === current) return;
    if (draft.length > FLAT_TYPE_MAX_LENGTH) {
      setFlatTypeErrorById((prev) => ({
        ...prev,
        [flat.id]: `Max ${FLAT_TYPE_MAX_LENGTH} characters`,
      }));
      return;
    }
    setFlatTypeSavingIds((prev) => new Set(prev).add(flat.id));
    setFlatTypeErrorById((prev) => {
      const next = { ...prev };
      delete next[flat.id];
      return next;
    });
    try {
      const res = await updateFlat(flat.id, { flat_type: draft || null });
      const saved = res.data?.flat_type ?? null;
      setData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          flats: prev.flats.map((f) =>
            f.id === flat.id ? { ...f, flat_type: saved } : f,
          ),
        };
      });
      setFlatTypeDrafts((prev) => {
        const next = { ...prev };
        delete next[flat.id];
        return next;
      });
    } catch (err: unknown) {
      setFlatTypeErrorById((prev) => ({
        ...prev,
        [flat.id]: err instanceof Error ? err.message : 'Failed to save',
      }));
    } finally {
      setFlatTypeSavingIds((prev) => {
        const next = new Set(prev);
        next.delete(flat.id);
        return next;
      });
    }
  };

  const stats = useMemo(() => {
    if (!data?.flats) return { total: 0, occupied: 0, vacant: 0, residents: 0 };
    const total = data.flats.length;
    let occupied = 0;
    let residents = 0;
    data.flats.forEach((f) => {
      const cached = membersCache[f.id];
      const userCount = cached !== undefined ? cached.length : f.users.length;
      if (userCount > 0) occupied++;
      residents += userCount;
    });
    return { total, occupied, vacant: total - occupied, residents };
  }, [data, membersCache]);

  const blockNames = useMemo(() => {
    if (!data?.flats) return [];
    const names = new Set<string>();
    data.flats.forEach((f) => {
      if (f.block_name) names.add(f.block_name);
    });
    return Array.from(names).sort();
  }, [data]);

  const filteredGroupedFlats = useMemo(() => {
    if (!data?.flats) return {};
    const q = searchQuery.toLowerCase().trim();

    const filtered = data.flats.filter((flat) => {
      if (blockFilter !== 'all' && (flat.block_name || 'Others') !== blockFilter) return false;

      if (!q) return true;

      if (flat.flat_number.toLowerCase().includes(q)) return true;
      if (flat.block_name?.toLowerCase().includes(q)) return true;

      const cached = membersCache[flat.id];
      const users = cached ?? flat.users;
      return users.some((u) => u.name.toLowerCase().includes(q));
    });

    const groups: Record<string, AdminSocietyFlat[]> = {};
    filtered.forEach((flat) => {
      const block = flat.block_name || 'Others';
      if (!groups[block]) groups[block] = [];
      groups[block].push(flat);
    });

    const sorted: Record<string, AdminSocietyFlat[]> = {};
    Object.keys(groups)
      .sort()
      .forEach((block) => {
        sorted[block] = groups[block].sort((a, b) => {
          if (a.floor !== b.floor) return (a.floor || 0) - (b.floor || 0);
          return a.flat_number.localeCompare(b.flat_number);
        });
      });
    return sorted;
  }, [data, searchQuery, blockFilter, membersCache]);

  const ensureMembersLoaded = async (flat: AdminSocietyFlat) => {
    if (membersCache[flat.id] !== undefined) return;
    setMembersLoadingIds((prev) => new Set(prev).add(flat.id));
    setMembersErrorByFlat((prev) => {
      const next = { ...prev };
      delete next[flat.id];
      return next;
    });
    try {
      const res = await getFlatMembers(flat.id);
      setMembersCache((prev) => ({ ...prev, [flat.id]: res.data }));
    } catch (e: unknown) {
      setMembersErrorByFlat((prev) => ({
        ...prev,
        [flat.id]: e instanceof Error ? e.message : 'Failed to load members',
      }));
    } finally {
      setMembersLoadingIds((prev) => {
        const next = new Set(prev);
        next.delete(flat.id);
        return next;
      });
    }
  };

  const toggleFlat = (flat: AdminSocietyFlat) => {
    setExpandedFlatIds((prev) => {
      const next = new Set(prev);
      if (next.has(flat.id)) {
        next.delete(flat.id);
      } else {
        next.add(flat.id);
        void ensureMembersLoaded(flat);
      }
      return next;
    });
  };

  const openRemovalModal = async (flat: AdminSocietyFlat, member: FlatMemberInfo) => {
    setRemovalModal({
      flat,
      member,
      checkData: null,
      checkLoading: true,
      checkError: '',
      removing: false,
      reason: '',
    });
    try {
      const res = await flatMemberRemovalCheck(flat.id, member.userId);
      setRemovalModal((prev) =>
        prev ? { ...prev, checkData: res.data, checkLoading: false } : null,
      );
    } catch (e: unknown) {
      setRemovalModal((prev) =>
        prev
          ? { ...prev, checkLoading: false, checkError: e instanceof Error ? e.message : 'Failed to check removal' }
          : null,
      );
    }
  };

  const confirmRemoval = async () => {
    if (!removalModal || !removalModal.checkData) return;
    setRemovalModal((prev) => (prev ? { ...prev, removing: true } : null));
    try {
      const res = await removeFlatMember(
        removalModal.flat.id,
        removalModal.member.userId,
        removalModal.reason || undefined,
      );
      if (!res.success) {
        setRemovalModal((prev) =>
          prev ? { ...prev, removing: false, checkError: res.message || 'Removal failed' } : null,
        );
        return;
      }
      const flatId = removalModal.flat.id;
      setRemovalModal(null);
      await refreshFlatMembers(flatId);
    } catch (e: unknown) {
      setRemovalModal((prev) =>
        prev
          ? { ...prev, removing: false, checkError: e instanceof Error ? e.message : 'Removal failed' }
          : null,
      );
    }
  };

  const refreshFlatMembers = async (flatId: number) => {
    try {
      const membersRes = await getFlatMembers(flatId);
      setMembersCache((prev) => ({ ...prev, [flatId]: membersRes.data }));
    } catch {
      setMembersCache((prev) => {
        const next = { ...prev };
        delete next[flatId];
        return next;
      });
    }
  };

  const openAddModal = async (flat: AdminSocietyFlat, e: React.MouseEvent) => {
    e.stopPropagation();
    setAddModal({
      flat,
      members: [],
      phone: '',
      name: '',
      roleInFlat: 'tenant',
      linkedToUserId: '',
      submitting: false,
      error: '',
    });
    try {
      const res = await getFlatMembers(flat.id);
      setMembersCache((prev) => ({ ...prev, [flat.id]: res.data }));
      const hasOwner = res.data.some((m) => normalizeRoleCategory(m.roleInFlat) === 'owner');
      setAddModal((prev) =>
        prev
          ? {
              ...prev,
              members: res.data,
              roleInFlat: hasOwner ? prev.roleInFlat : 'owner',
            }
          : null,
      );
    } catch (err: unknown) {
      setAddModal((prev) =>
        prev
          ? {
              ...prev,
              error: err instanceof Error ? err.message : 'Failed to load flat members',
            }
          : null,
      );
    }
  };

  const submitAddMember = async () => {
    if (!addModal) return;

    const phoneDigits = addModal.phone.replace(/\D/g, '').slice(-10);
    if (phoneDigits.length !== 10) {
      setAddModal((prev) => (prev ? { ...prev, error: 'Enter a valid 10-digit phone number' } : null));
      return;
    }
    if (!addModal.name.trim()) {
      setAddModal((prev) => (prev ? { ...prev, error: 'Name is required' } : null));
      return;
    }
    if (addModal.roleInFlat === 'family_member' && addModal.linkedToUserId === '') {
      setAddModal((prev) =>
        prev ? { ...prev, error: 'Select an owner or tenant to link this family member to' } : null,
      );
      return;
    }

    setAddModal((prev) => (prev ? { ...prev, submitting: true, error: '' } : null));
    try {
      const res = await adminAddFlatMember(addModal.flat.id, {
        phone: phoneDigits,
        name: addModal.name.trim(),
        roleInFlat: addModal.roleInFlat,
        linkedToUserId:
          addModal.roleInFlat === 'family_member' ? Number(addModal.linkedToUserId) : null,
      });
      if (!res.success) {
        setAddModal((prev) =>
          prev ? { ...prev, submitting: false, error: res.message || 'Failed to add member' } : null,
        );
        return;
      }
      const flatId = addModal.flat.id;
      setAddModal(null);
      await refreshFlatMembers(flatId);
      if (!expandedFlatIds.has(flatId)) {
        setExpandedFlatIds((prev) => new Set(prev).add(flatId));
      }
    } catch (err: unknown) {
      setAddModal((prev) =>
        prev
          ? {
              ...prev,
              submitting: false,
              error: err instanceof Error ? err.message : 'Failed to add member',
            }
          : null,
      );
    }
  };

  const linkableMembers = useMemo(() => {
    if (!addModal) return [];
    return addModal.members.filter((m) => {
      const cat = normalizeRoleCategory(m.roleInFlat);
      return cat === 'owner' || cat === 'co_owner' || cat === 'tenant';
    });
  }, [addModal]);

  const canAddOwner = useMemo(() => {
    if (!addModal) return false;
    return !addModal.members.some((m) => normalizeRoleCategory(m.roleInFlat) === 'owner');
  }, [addModal]);

  const totalFilteredFlats = useMemo(() => {
    return Object.values(filteredGroupedFlats).reduce((sum, arr) => sum + arr.length, 0);
  }, [filteredGroupedFlats]);

  return (
    <div className="saflats-page">
      <div className="saflats-top-bar">
        <div className="saflats-search-row">
          <div className="saflats-search-wrap">
            <HiSearch size={18} className="saflats-search-icon" />
            <input
              type="text"
              className="saflats-search-input"
              placeholder="Search flats, blocks, or residents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="saflats-search-clear" onClick={() => setSearchQuery('')}>
                <HiX size={14} />
              </button>
            )}
          </div>
          <select
            className="saflats-block-filter"
            value={blockFilter}
            onChange={(e) => setBlockFilter(e.target.value)}
          >
            <option value="all">All Blocks</option>
            {blockNames.map((b) => (
              <option key={b} value={b}>
                Block {b}
              </option>
            ))}
          </select>
          <button
            className="saflats-refresh-btn"
            onClick={() => {
              setMembersCache({});
              setExpandedFlatIds(new Set());
              fetchData();
            }}
            disabled={loading}
          >
            <HiRefresh size={16} className={loading ? 'saflats-spin' : ''} />
            Refresh
          </button>
          <button
            type="button"
            className="saflats-export-btn"
            onClick={() => void handleExportMembers()}
            disabled={loading || exporting || !societyId}
            title="Download Excel with owner, tenant, and family members (bulk upload format)"
          >
            <HiDownload size={16} className={exporting ? 'saflats-spin' : ''} />
            {exporting ? 'Exporting…' : 'Export Members'}
          </button>
        </div>
        {exportError && (
          <div className="saflats-alert" style={{ marginTop: 12, marginBottom: 0 }}>
            <HiExclamation size={18} />
            {exportError}
          </div>
        )}
      </div>

      <div className="saflats-stats-row">
        <div className="saflats-stat-card">
          <span className="saflats-stat-value">{stats.total}</span>
          <span className="saflats-stat-label">Total Flats</span>
        </div>
        <div className="saflats-stat-card saflats-stat-occupied">
          <span className="saflats-stat-value">{stats.occupied}</span>
          <span className="saflats-stat-label">Occupied</span>
        </div>
        <div className="saflats-stat-card saflats-stat-vacant">
          <span className="saflats-stat-value">{stats.vacant}</span>
          <span className="saflats-stat-label">Vacant</span>
        </div>
        <div className="saflats-stat-card saflats-stat-residents">
          <span className="saflats-stat-value">{stats.residents}</span>
          <span className="saflats-stat-label">Total Residents</span>
        </div>
      </div>

      {error && (
        <div className="saflats-alert">
          <HiExclamation size={18} />
          <span>{error}</span>
        </div>
      )}

      {loading && (
        <div className="saflats-loading">
          <HiRefresh size={28} className="saflats-spin" />
          <p>Loading flats data...</p>
        </div>
      )}

      {!loading && !error && data && (
        <div className="saflats-content">
          {totalFilteredFlats === 0 ? (
            <div className="saflats-empty">
              <HiUserGroup size={40} />
              <p>
                {searchQuery || blockFilter !== 'all'
                  ? 'No flats match your search or filter.'
                  : 'No flats found in this society.'}
              </p>
            </div>
          ) : (
            Object.entries(filteredGroupedFlats).map(([blockName, flats]) => (
              <div key={blockName} className="saflats-block-section">
                <div className="saflats-block-header">
                  <h3 className="saflats-block-title">Block {blockName}</h3>
                  <span className="saflats-block-count">{flats.length} flats</span>
                </div>

                <div className="saflats-table-container">
                  <table className="saflats-table">
                    <thead>
                      <tr>
                        <th style={{ width: 40 }}></th>
                        <th>Flat No.</th>
                        <th>Floor</th>
                        <th>Area</th>
                        <th>Flat type</th>
                        <th>Residents</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {flats.map((flat) => {
                        const expanded = expandedFlatIds.has(flat.id);
                        const cached = membersCache[flat.id];
                        const userCount = cached !== undefined ? cached.length : flat.users.length;
                        const occupied = userCount > 0;
                        const membersLoading = membersLoadingIds.has(flat.id);
                        const membersErr = membersErrorByFlat[flat.id];
                        const typeSaving = flatTypeSavingIds.has(flat.id);
                        const typeErr = flatTypeErrorById[flat.id];

                        return (
                          <React.Fragment key={flat.id}>
                            <tr
                              className={`saflats-row ${expanded ? 'saflats-row--expanded' : ''} ${occupied ? '' : 'saflats-row--vacant'}`}
                              onClick={() => toggleFlat(flat)}
                            >
                              <td className="saflats-td-chevron">
                                {expanded ? <HiChevronUp size={16} /> : <HiChevronDown size={16} />}
                              </td>
                              <td className="saflats-td-flat-number">{flat.flat_number}</td>
                              <td>{flat.floor}</td>
                              <td>{flat.area != null ? `${flat.area} sq ft` : '-'}</td>
                              <td
                                className="saflats-td-flat-type"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <input
                                  type="text"
                                  className="saflats-flat-type-input"
                                  value={getFlatTypeDraft(flat)}
                                  placeholder="e.g. 2 BHK"
                                  maxLength={FLAT_TYPE_MAX_LENGTH}
                                  disabled={typeSaving}
                                  onChange={(e) =>
                                    setFlatTypeDrafts((prev) => ({
                                      ...prev,
                                      [flat.id]: e.target.value,
                                    }))
                                  }
                                  onBlur={() => void saveFlatType(flat)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      (e.target as HTMLInputElement).blur();
                                    }
                                  }}
                                />
                                {typeSaving && <span className="saflats-flat-type-hint">Saving…</span>}
                                {typeErr && <span className="saflats-flat-type-error">{typeErr}</span>}
                              </td>
                              <td>
                                <span className="saflats-residents-badge">
                                  <HiUsers size={14} />
                                  {userCount}
                                </span>
                              </td>
                              <td>
                                <span className={`saflats-status-badge ${occupied ? 'saflats-status-occupied' : 'saflats-status-vacant'}`}>
                                  {occupied ? 'Occupied' : 'Vacant'}
                                </span>
                              </td>
                            </tr>

                            {expanded && (
                              <tr className="saflats-expanded-row">
                                <td colSpan={7}>
                                  <div className="saflats-expanded-panel">
                                    <div className="saflats-panel-toolbar">
                                      <button
                                        className="saflats-add-btn"
                                        onClick={(e) => openAddModal(flat, e)}
                                      >
                                        <HiPlus size={14} />
                                        Add member
                                      </button>
                                    </div>
                                    {membersLoading && !cached && (
                                      <div className="saflats-panel-loading">
                                        <HiRefresh size={16} className="saflats-spin" /> Loading members...
                                      </div>
                                    )}
                                    {membersErr && (
                                      <div className="saflats-panel-error">
                                        <HiExclamation size={16} /> {membersErr}
                                      </div>
                                    )}
                                    {cached && cached.length === 0 && (
                                      <div className="saflats-panel-empty">No members linked to this flat.</div>
                                    )}
                                    {cached && cached.length > 0 && (
                                      <div className="saflats-members-grid">
                                        {cached.map((m) => {
                                          const cat = normalizeRoleCategory(m.roleInFlat);
                                          return (
                                            <div key={m.userFlatId} className="saflats-member-card">
                                              <div className="saflats-member-top">
                                                <div className="saflats-member-info">
                                                  <span className="saflats-member-name">{m.name}</span>
                                                  <div className="saflats-member-badges">
                                                    <span className={`saflats-role-badge saflats-role-${cat}`}>
                                                      {roleBadgeLabel(cat)}
                                                    </span>
                                                    {m.isPrimaryTenant && (
                                                      <span className="saflats-primary-badge">Primary</span>
                                                    )}
                                                    <span className={`saflats-approval-badge ${m.isApproved ? 'saflats-approved' : 'saflats-pending'}`}>
                                                      {m.isApproved ? 'Approved' : 'Pending'}
                                                    </span>
                                                  </div>
                                                </div>
                                                <button
                                                  className="saflats-remove-btn"
                                                  title="Remove member"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    openRemovalModal(flat, m);
                                                  }}
                                                >
                                                  <HiTrash size={14} />
                                                </button>
                                              </div>
                                              <div className="saflats-member-details">
                                                {m.phone && <span>{m.phone}</span>}
                                                {m.email && <span>{m.email}</span>}
                                                {m.joinedAt && <span>Joined {formatDate(m.joinedAt)}</span>}
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {addModal && (
        <div className="saflats-modal-overlay" onClick={() => !addModal.submitting && setAddModal(null)}>
          <div className="saflats-modal saflats-modal--add" onClick={(e) => e.stopPropagation()}>
            <div className="saflats-modal-header saflats-modal-header--add">
              <h3>Add Member</h3>
              <button
                className="saflats-modal-close"
                onClick={() => !addModal.submitting && setAddModal(null)}
                disabled={addModal.submitting}
              >
                <HiX size={18} />
              </button>
            </div>
            <div className="saflats-modal-body">
              <p className="saflats-modal-flat-label">
                Flat {addModal.flat.flat_number}
                {addModal.flat.block_name ? ` · Block ${addModal.flat.block_name}` : ''}
              </p>
              <p className="saflats-modal-note">The resident will not be notified.</p>

              {addModal.error && (
                <div className="saflats-modal-error">
                  <HiExclamation size={18} />
                  <span>{addModal.error}</span>
                </div>
              )}

              <div className="saflats-form-field">
                <label htmlFor="add-member-phone">Phone</label>
                <input
                  id="add-member-phone"
                  type="tel"
                  className="saflats-form-input"
                  placeholder="10-digit mobile number"
                  value={addModal.phone}
                  onChange={(e) =>
                    setAddModal((prev) => (prev ? { ...prev, phone: e.target.value, error: '' } : null))
                  }
                  disabled={addModal.submitting}
                />
              </div>

              <div className="saflats-form-field">
                <label htmlFor="add-member-name">Full name</label>
                <input
                  id="add-member-name"
                  type="text"
                  className="saflats-form-input"
                  placeholder="Resident name"
                  value={addModal.name}
                  onChange={(e) =>
                    setAddModal((prev) => (prev ? { ...prev, name: e.target.value, error: '' } : null))
                  }
                  disabled={addModal.submitting}
                />
              </div>

              <div className="saflats-form-field">
                <label htmlFor="add-member-role">Role</label>
                <select
                  id="add-member-role"
                  className="saflats-form-input"
                  value={addModal.roleInFlat}
                  onChange={(e) => {
                    const roleInFlat = e.target.value as FlatMemberRole;
                    setAddModal((prev) =>
                      prev
                        ? {
                            ...prev,
                            roleInFlat,
                            linkedToUserId: roleInFlat === 'family_member' ? prev.linkedToUserId : '',
                            error: '',
                          }
                        : null,
                    );
                  }}
                  disabled={addModal.submitting}
                >
                  {canAddOwner && <option value="owner">Owner</option>}
                  <option value="co_owner">Co-owner</option>
                  <option value="tenant">Tenant</option>
                  <option value="family_member">Family member</option>
                </select>
              </div>

              {addModal.roleInFlat === 'family_member' && (
                <div className="saflats-form-field">
                  <label htmlFor="add-member-linked">Linked to</label>
                  <select
                    id="add-member-linked"
                    className="saflats-form-input"
                    value={addModal.linkedToUserId}
                    onChange={(e) =>
                      setAddModal((prev) =>
                        prev
                          ? {
                              ...prev,
                              linkedToUserId: e.target.value === '' ? '' : Number(e.target.value),
                              error: '',
                            }
                          : null,
                      )
                    }
                    disabled={addModal.submitting || linkableMembers.length === 0}
                  >
                    <option value="">Select owner or tenant</option>
                    {linkableMembers.map((m) => (
                      <option key={m.userId} value={m.userId}>
                        {m.name} ({roleBadgeLabel(normalizeRoleCategory(m.roleInFlat))})
                      </option>
                    ))}
                  </select>
                  {linkableMembers.length === 0 && (
                    <span className="saflats-field-hint">Add an owner or tenant first.</span>
                  )}
                </div>
              )}

              <div className="saflats-modal-actions">
                <button
                  className="saflats-btn saflats-btn-cancel"
                  onClick={() => setAddModal(null)}
                  disabled={addModal.submitting}
                >
                  Cancel
                </button>
                <button
                  className="saflats-btn saflats-btn-primary"
                  onClick={submitAddMember}
                  disabled={
                    addModal.submitting ||
                    (addModal.roleInFlat === 'family_member' && linkableMembers.length === 0)
                  }
                >
                  {addModal.submitting ? 'Adding...' : 'Add Member'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {removalModal && (
        <div className="saflats-modal-overlay" onClick={() => !removalModal.removing && setRemovalModal(null)}>
          <div className="saflats-modal" onClick={(e) => e.stopPropagation()}>
            <div className="saflats-modal-header">
              <h3>Remove Member</h3>
              <button
                className="saflats-modal-close"
                onClick={() => !removalModal.removing && setRemovalModal(null)}
                disabled={removalModal.removing}
              >
                <HiX size={18} />
              </button>
            </div>
            <div className="saflats-modal-body">
              {removalModal.checkLoading && (
                <div className="saflats-modal-loading">
                  <HiRefresh size={20} className="saflats-spin" />
                  <span>Checking removal impact...</span>
                </div>
              )}

              {removalModal.checkError && (
                <div className="saflats-modal-error">
                  <HiExclamation size={18} />
                  <span>{removalModal.checkError}</span>
                </div>
              )}

              {removalModal.checkData && (
                <>
                  <div className="saflats-modal-member-info">
                    <p>
                      <strong>{removalModal.checkData.name}</strong> ({removalModal.checkData.roleInFlat})
                    </p>
                    <p>Flat {removalModal.checkData.flatNumber}</p>
                    {removalModal.checkData.phone && <p>{removalModal.checkData.phone}</p>}
                  </div>

                  {removalModal.checkData.pendingBillCount > 0 && (
                    <div className="saflats-modal-warning">
                      <HiExclamation size={16} />
                      <div>
                        <strong>{removalModal.checkData.pendingBillCount} pending bill(s)</strong> totalling{' '}
                        {formatRupees(removalModal.checkData.pendingBillTotal)} will remain unpaid.
                      </div>
                    </div>
                  )}

                  {removalModal.checkData.cascadeMemberCount > 0 && (
                    <div className="saflats-modal-warning">
                      <HiExclamation size={16} />
                      <div>
                        <strong>{removalModal.checkData.cascadeMemberCount} linked member(s)</strong> will also be
                        removed:
                        <ul className="saflats-cascade-list">
                          {removalModal.checkData.cascadeMembers.map((cm) => (
                            <li key={cm.userFlatId}>
                              {cm.name} ({cm.roleInFlat})
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  <div className="saflats-modal-reason">
                    <label htmlFor="removal-reason">Reason (optional)</label>
                    <textarea
                      id="removal-reason"
                      className="saflats-reason-input"
                      placeholder="Enter reason for removal..."
                      value={removalModal.reason}
                      onChange={(e) =>
                        setRemovalModal((prev) => (prev ? { ...prev, reason: e.target.value } : null))
                      }
                      rows={3}
                    />
                  </div>

                  <div className="saflats-modal-actions">
                    <button
                      className="saflats-btn saflats-btn-cancel"
                      onClick={() => setRemovalModal(null)}
                      disabled={removalModal.removing}
                    >
                      Cancel
                    </button>
                    <button
                      className="saflats-btn saflats-btn-danger"
                      onClick={confirmRemoval}
                      disabled={removalModal.removing}
                    >
                      {removalModal.removing ? 'Removing...' : 'Remove Member'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SocietyAdminFlats;
